import type { AxiosResponse } from "axios";
import { apiPathPrefix } from "../config/onPipeConfigurator/appConfig";
import type {
  BuildSpecification,
  CadCheck,
  ConfigurationResult,
  ConfigurationService,
  EngineeringOverride,
  EquipmentKind,
  ProjectSpecification,
  ValidationError,
} from "../components/OnPipeConfigurator/models/types";
import axiosConfig from "./onPipeConfiguratorAxiosConfig";

const API = apiPathPrefix;

interface ApiEnvelope<T> {
  data: T;
  message: string;
}

export class OnPipeApiError extends Error {
  readonly status: number;

  readonly data: unknown;

  readonly validationErrors?: ValidationError[];

  constructor(status: number, message: string, data: unknown = null) {
    super(message);
    this.name = "OnPipeApiError";
    this.status = status;
    this.data = data;
    if (data && typeof data === "object" && "errors" in data) {
      const errors = (data as { errors?: ValidationError[] }).errors;
      if (Array.isArray(errors)) {
        this.validationErrors = errors;
      }
    }
  }
}

function unwrapApiPayload<T>(response: AxiosResponse): T {
  const body = response.data;
  if (body && typeof body === "object" && "data" in body && "message" in body) {
    return body.data as T;
  }
  return body as T;
}

async function apiGet<T>(path: string): Promise<T> {
  try {
    const response = await axiosConfig.get(`${API}${path}`, {
      headers: { Accept: "application/json" },
    });
    return unwrapApiPayload<T>(response);
  } catch (error) {
    throw toApiError(error);
  }
}

async function apiPost<T>(path: string, payload: unknown): Promise<T> {
  try {
    const response = await axiosConfig.post(`${API}${path}`, payload, {
      headers: { "Content-Type": "application/json", Accept: "application/json" },
    });
    return unwrapApiPayload<T>(response);
  } catch (error) {
    throw toApiError(error);
  }
}

async function apiPut<T>(path: string, payload: unknown): Promise<T> {
  try {
    const response = await axiosConfig.put(`${API}${path}`, payload, {
      headers: { "Content-Type": "application/json", Accept: "application/json" },
    });
    return unwrapApiPayload<T>(response);
  } catch (error) {
    throw toApiError(error);
  }
}

function toApiError(error: unknown): OnPipeApiError {
  if (error && typeof error === "object" && "response" in error) {
    const axiosError = error as {
      response?: { status?: number; data?: ApiEnvelope<unknown> };
      message?: string;
    };
    const status = axiosError.response?.status ?? 500;
    const body = axiosError.response?.data;
    const message =
      body && typeof body === "object" && "message" in body && typeof body.message === "string"
        ? body.message
        : axiosError.message ?? "Request failed.";
    const data = body && typeof body === "object" && "data" in body ? body.data : null;
    return new OnPipeApiError(status, message, data);
  }
  if (error instanceof Error) {
    return new OnPipeApiError(500, error.message);
  }
  return new OnPipeApiError(500, "Request failed.");
}

export const onPipeConfiguratorApi: ConfigurationService = {
  async listConfigurations() {
    return apiGet<ConfigurationResult[]>("/configurations");
  },

  async createConfiguration(project: ProjectSpecification) {
    return apiPost<ConfigurationResult>("/configurations", project);
  },

  async getConfiguration(configurationId: string) {
    return apiGet<ConfigurationResult>(`/configurations/${encodeURIComponent(configurationId)}`);
  },

  async getBuildSpecification(configurationId: string, equipment: EquipmentKind) {
    const query = new URLSearchParams({ equipment }).toString();
    return apiGet<BuildSpecification>(
      `/configurations/${encodeURIComponent(configurationId)}/build-spec?${query}`,
    );
  },

  async updateCadChecks(configurationId: string, equipment: EquipmentKind, checks: CadCheck[]) {
    return apiPut<ConfigurationResult>(
      `/configurations/${encodeURIComponent(configurationId)}/cad-checks`,
      { equipment, checks },
    );
  },

  async applyOverride(
    configurationId: string,
    equipment: EquipmentKind,
    override: EngineeringOverride,
  ) {
    return apiPost<ConfigurationResult>(
      `/configurations/${encodeURIComponent(configurationId)}/overrides`,
      { equipment, override },
    );
  },
};
